import { z } from 'zod';
import { content as defaultContent } from '../content';
import type { ContentRegistry, GameState } from '../core/types';

export const SAVE_KEY = 'ironvein.run.v1';
const SAVE_FORMAT = 'ironvein-save';

export interface SaveStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

const statusMapSchema = z.object({
  strength: z.number().int().nonnegative(),
  dexterity: z.number().int().nonnegative(),
  weak: z.number().int().nonnegative(),
  vulnerable: z.number().int().nonnegative(),
  burning: z.number().int().nonnegative(),
  thorns: z.number().int().nonnegative(),
});

const cardInstanceSchema = z.object({
  id: z.string().min(1),
  definitionId: z.string().min(1),
  upgraded: z.boolean(),
  durability: z.number().int().nonnegative().optional(),
  temporary: z.boolean().optional(),
});

const enemyInstanceSchema = z.object({
  id: z.string().min(1),
  definitionId: z.string().min(1),
  hp: z.number().int().nonnegative(),
  maxHp: z.number().int().positive(),
  block: z.number().int().nonnegative(),
  statuses: statusMapSchema,
  moveIndex: z.number().int().nonnegative(),
  phaseIndex: z.number().int().nonnegative(),
});

const runSchema = z.object({
  hp: z.number().int().nonnegative(),
  maxHp: z.number().int().positive(),
  iron: z.number().int().nonnegative(),
  difficulty: z.enum(['standard', 'deepening']),
  act: z.number().int().min(1).max(3),
  actStep: z.number().int().min(0).max(4),
  node: z.number().int().nonnegative(),
  deck: z.array(cardInstanceSchema).min(1),
  relics: z.array(z.string()),
  combatsWon: z.number().int().nonnegative(),
  bossesDefeated: z.number().int().nonnegative(),
  encountersSeen: z.array(z.string()),
  routeOptions: z.array(
    z.object({
      id: z.string().min(1),
      kind: z.enum(['combat', 'elite', 'boss', 'event', 'trader', 'forge']),
      label: z.string().min(1),
      description: z.string().min(1),
      encounterId: z.string().optional(),
      eventId: z.string().optional(),
    }),
  ),
});

const combatSchema = z.object({
  encounterId: z.string().min(1),
  phase: z.enum(['player', 'enemy', 'won', 'lost']),
  turn: z.number().int().positive(),
  energy: z.number().int().nonnegative(),
  maxEnergy: z.number().int().positive(),
  heat: z.number().int().min(0).max(6),
  block: z.number().int().nonnegative(),
  statuses: statusMapSchema,
  hand: z.array(z.string()),
  drawPile: z.array(z.string()),
  discardPile: z.array(z.string()),
  exhaustPile: z.array(z.string()),
  enemies: z.array(enemyInstanceSchema).min(1),
  gearPlayedThisCombat: z.number().int().nonnegative(),
});

const gameStateSchema = z.object({
  schemaVersion: z.literal(1),
  contentVersion: z.string().min(1),
  screen: z.enum([
    'title',
    'route',
    'combat',
    'reward',
    'event',
    'trader',
    'forge',
    'defeat',
    'victory',
  ]),
  seed: z.string(),
  rng: z.number().int().nonnegative(),
  nextId: z.number().int().positive(),
  run: runSchema.optional(),
  combat: combatSchema.optional(),
  reward: z
    .object({
      kind: z.enum(['normal', 'elite', 'boss']),
      cardIds: z.array(z.string()).max(3),
      relicIds: z.array(z.string()).max(3),
      iron: z.number().int().nonnegative(),
    })
    .optional(),
  event: z.object({
    eventId: z.string().min(1),
    pendingAction: z.enum(['upgrade', 'remove']).optional(),
    pendingChoiceId: z.string().optional(),
  }).optional(),
  shop: z
    .object({
      cards: z.array(
        z.object({
          id: z.string().min(1),
          cardDefinitionId: z.string().min(1),
          price: z.number().int().positive(),
          sold: z.boolean(),
        }),
      ),
      relic: z
        .object({
          id: z.string().min(1),
          relicId: z.string().min(1),
          price: z.number().int().positive(),
          sold: z.boolean(),
        })
        .optional(),
      removalPrice: z.number().int().positive(),
      repairPrice: z.number().int().positive(),
      removalUsed: z.boolean(),
    })
    .optional(),
});

const saveEnvelopeSchema = z.object({
  format: z.literal(SAVE_FORMAT),
  schemaVersion: z.literal(1),
  savedAt: z.string().datetime(),
  state: gameStateSchema,
});

export class SaveError extends Error {
  constructor(message: string, options?: ErrorOptions) {
    super(message, options);
    this.name = 'SaveError';
  }
}

function assertKnownContent(state: GameState, registry: ContentRegistry): void {
  if (state.contentVersion !== registry.version) {
    throw new SaveError(
      `Save content version ${state.contentVersion} does not match ${registry.version}.`,
    );
  }

  if (!state.run) {
    if (state.screen !== 'title') throw new SaveError('A non-title save is missing its run state.');
    return;
  }

  switch (state.screen) {
    case 'title':
      break;
    case 'route':
      if (state.run.routeOptions.length === 0) {
        throw new SaveError('A route save has no available route options.');
      }
      break;
    case 'combat':
      if (!state.combat) throw new SaveError('A combat save is missing its combat state.');
      if (state.combat.phase !== 'player') {
        throw new SaveError('A combat save is not at a resumable player turn.');
      }
      break;
    case 'reward':
      if (!state.reward || !state.combat) {
        throw new SaveError('A reward save is missing its reward or combat state.');
      }
      if (state.combat.phase !== 'won') {
        throw new SaveError('A reward save does not contain a won combat.');
      }
      break;
    case 'event':
      if (!state.event) throw new SaveError('An event save is missing its event state.');
      break;
    case 'trader':
      if (!state.shop) throw new SaveError('A trader save is missing its shop state.');
      break;
    case 'forge':
    case 'defeat':
    case 'victory':
      break;
  }

  const instanceIds = new Set<string>();
  for (const card of state.run.deck) {
    if (instanceIds.has(card.id)) throw new SaveError(`Duplicate card instance id: ${card.id}`);
    instanceIds.add(card.id);
    const definition = registry.cards[card.definitionId];
    if (!definition) throw new SaveError(`Save references unknown card: ${card.definitionId}`);
    if (definition.gear) {
      const maxDurability =
        card.upgraded && definition.upgrade?.gear
          ? definition.upgrade.gear.maxDurability
          : definition.gear.maxDurability;
      if (card.durability === undefined || card.durability > maxDurability) {
        throw new SaveError(`Invalid Durability for card: ${card.id}`);
      }
    } else if (card.durability !== undefined) {
      throw new SaveError(`Non-Gear card has Durability: ${card.id}`);
    }
  }

  for (const relicId of state.run.relics) {
    if (!registry.relics[relicId]) throw new SaveError(`Save references unknown relic: ${relicId}`);
  }
  for (const option of state.run.routeOptions) {
    if (option.encounterId && !registry.encounters[option.encounterId]) {
      throw new SaveError(`Route references unknown encounter: ${option.encounterId}`);
    }
    if (option.eventId && !registry.events[option.eventId]) {
      throw new SaveError(`Route references unknown event: ${option.eventId}`);
    }
  }
  for (const encounterId of state.run.encountersSeen) {
    if (!registry.encounters[encounterId]) {
      throw new SaveError(`Encounter history references unknown encounter: ${encounterId}`);
    }
  }

  if (state.combat) {
    if (!registry.encounters[state.combat.encounterId]) {
      throw new SaveError(`Save references unknown encounter: ${state.combat.encounterId}`);
    }
    for (const enemy of state.combat.enemies) {
      if (!registry.enemies[enemy.definitionId]) {
        throw new SaveError(`Save references unknown enemy: ${enemy.definitionId}`);
      }
    }
    const pileIds = [
      ...state.combat.hand,
      ...state.combat.drawPile,
      ...state.combat.discardPile,
      ...state.combat.exhaustPile,
    ];
    if (new Set(pileIds).size !== pileIds.length) {
      throw new SaveError('A card instance appears in more than one combat pile.');
    }
    if (pileIds.length !== instanceIds.size) {
      throw new SaveError('A combat save is missing one or more deck cards from its piles.');
    }
    for (const cardId of pileIds) {
      if (!instanceIds.has(cardId)) throw new SaveError(`Combat references unknown card: ${cardId}`);
    }
  }

  if (state.reward) {
    for (const cardId of state.reward.cardIds) {
      if (!registry.cards[cardId]) throw new SaveError(`Reward references unknown card: ${cardId}`);
    }
    for (const relicId of state.reward.relicIds) {
      if (!registry.relics[relicId]) throw new SaveError(`Reward references unknown relic: ${relicId}`);
    }
  }

  if (state.event) {
    const event = registry.events[state.event.eventId];
    if (!event) throw new SaveError(`Save references unknown event: ${state.event.eventId}`);
    if (Boolean(state.event.pendingAction) !== Boolean(state.event.pendingChoiceId)) {
      throw new SaveError('Event card selection is incomplete.');
    }
    if (state.event.pendingAction && state.event.pendingChoiceId) {
      const choice = event.choices.find((candidate) => candidate.id === state.event?.pendingChoiceId);
      const expectedEffect = state.event.pendingAction === 'upgrade' ? 'choose_upgrade' : 'choose_remove';
      if (!choice?.effects.some((effect) => effect.kind === expectedEffect)) {
        throw new SaveError('Event card selection does not match its event choice.');
      }
    }
  }

  if (state.shop) {
    for (const offer of state.shop.cards) {
      if (!registry.cards[offer.cardDefinitionId]) {
        throw new SaveError(`Shop references unknown card: ${offer.cardDefinitionId}`);
      }
    }
    if (state.shop.relic && !registry.relics[state.shop.relic.relicId]) {
      throw new SaveError(`Shop references unknown relic: ${state.shop.relic.relicId}`);
    }
  }
}

export function serializeSave(state: GameState, savedAt = new Date()): string {
  return JSON.stringify({
    format: SAVE_FORMAT,
    schemaVersion: 1,
    savedAt: savedAt.toISOString(),
    state,
  });
}

export function parseSave(
  serialized: string,
  registry: ContentRegistry = defaultContent,
): GameState {
  let parsed: unknown;
  try {
    parsed = JSON.parse(serialized);
  } catch (error) {
    throw new SaveError('The saved run is not valid JSON.', { cause: error });
  }

  const envelope = saveEnvelopeSchema.safeParse(parsed);
  if (!envelope.success) {
    throw new SaveError(`The saved run has an invalid structure: ${z.prettifyError(envelope.error)}`);
  }

  const state = envelope.data.state as GameState;
  assertKnownContent(state, registry);
  return structuredClone(state);
}

export function saveGame(
  state: GameState,
  storage: SaveStorage = window.localStorage,
): void {
  if (!state.run || state.screen === 'title') return;
  storage.setItem(SAVE_KEY, serializeSave(state));
}

export function loadGame(
  storage: SaveStorage = window.localStorage,
  registry: ContentRegistry = defaultContent,
): GameState | undefined {
  const serialized = storage.getItem(SAVE_KEY);
  return serialized ? parseSave(serialized, registry) : undefined;
}

export function clearSave(storage: SaveStorage = window.localStorage): void {
  storage.removeItem(SAVE_KEY);
}
